import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/favorites/", label: "Vault" },
  { to: "/about/", label: "About" },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumePw, setResumePw] = useState("");
  const [resumeErr, setResumeErr] = useState(null);
  const [resumeBusy, setResumeBusy] = useState(false);

  const closeMenu = () => {
    if (!menuOpen) return;
    setMenuClosing(true);
    setTimeout(() => {
      setMenuOpen(false);
      setMenuClosing(false);
    }, 220);
  };

  const toggleMenu = () => {
    if (menuOpen) {
      closeMenu();
    } else {
      setMenuClosing(false);
      setMenuOpen(true);
    }
  };

  const downloadResume = async () => {
    if (!resumePw || resumeBusy) return;
    setResumeBusy(true);
    setResumeErr(null);
    try {
      const res = await fetch("/api/resume", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: resumePw }),
      });
      if (res.status === 401) {
        setResumeErr("Incorrect password");
        return;
      }
      if (!res.ok) {
        setResumeErr("Something went wrong. Try again.");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "resume.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setResumeOpen(false);
      setResumePw("");
    } catch {
      setResumeErr("Could not reach Charlie's agent server. Try again.");
    } finally {
      setResumeBusy(false);
    }
  };

  const closeResume = () => {
    setResumeOpen(false);
    setResumePw("");
    setResumeErr(null);
  };

  useEffect(() => {
    if (!resumeOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeResume();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [resumeOpen]);

  const openChatAgent = (e) => {
    e.preventDefault();
    closeResume();
    window.dispatchEvent(new CustomEvent("open-chat"));
  };

  const onResumeKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      downloadResume();
    }
  };

  return (
    <>
      <header className="site-header">
        <nav className="header-nav" aria-label="Primary">
          <Link to="/" className="wordmark">
            charliegp<span className="wordmark-dev">.dev</span>
          </Link>
          <ul className="header-links">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  onClick={closeMenu}
                  className={({ isActive }) => "header-link" + (isActive ? " active" : "")}
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <ThemeToggle />
          <button
            className="header-link resume-link"
            onClick={() => { setResumeOpen(true); closeMenu(); }}
            aria-haspopup="dialog"
            title="Password protected"
          >
            resume.pdf
          </button>
          <button
            className={"menu-toggle" + (menuOpen ? " open" : "")}
            onClick={toggleMenu}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            <span></span><span></span><span></span>
          </button>
        </nav>
      </header>

      {(menuOpen || menuClosing) && (
        <div className={"mobile-menu" + (menuClosing ? " closing" : "")}>
          <ul className="mobile-menu-links">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  onClick={closeMenu}
                  className={({ isActive }) => "header-link" + (isActive ? " active" : "")}
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li>
              <button
                className="header-link resume-link"
                onClick={() => { setResumeOpen(true); closeMenu(); }}
                title="Password protected"
              >
                resume.pdf
              </button>
            </li>
          </ul>
        </div>
      )}

      {resumeOpen && (
        <div className="resume-overlay" role="presentation" onMouseDown={closeResume}>
          <div
            className="resume-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Download resume"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <p className="resume-modal-title">Download resume</p>
            <p className="resume-modal-sub">Enter the password to access resume.pdf.</p>
            <p className="resume-modal-hint">
              Need the password?{" "}
              <button type="button" className="resume-hint-link" onClick={openChatAgent}>
                Ask Charlie's agent
              </button>{" "}
              or{" "}
              <a href="mailto:charliepandacan@gmail.com" className="resume-hint-link">
                send an email
              </a>
              .
            </p>
            <input
              className="resume-modal-input"
              type="password"
              value={resumePw}
              onChange={(e) => setResumePw(e.target.value)}
              onKeyDown={onResumeKey}
              placeholder="Password"
              autoFocus
              aria-label="Resume password"
            />
            {resumeErr && <p className="resume-modal-error">{resumeErr}</p>}
            <div className="resume-modal-actions">
              <button className="resume-modal-btn" onClick={closeResume}>
                Cancel
              </button>
              <button className="resume-modal-btn primary" onClick={downloadResume} disabled={!resumePw || resumeBusy}>
                {resumeBusy ? "Loading…" : "Download"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}